import { ComponentType } from 'react';
import Trading212Icon from '../components/icons/Trading212Icon';
// Commented out unused icons
/*
import {
  AccountBalance as FreetradIcon,
  Business as InteractiveBrokersIcon,
  Store as EToro,
  CreditCard as RobinhoodIcon,
  AccountBalance as DegiroIcon,
  Business as FidelityIcon,
  AccountBalance as SchwabIcon,
  AccountBalance as TDAmeritrade,
  Public as PublicIcon,
  CreditCard as WebrulIcon,
  Business as TastytradIcon,
} from "@mui/icons-material";
*/

export type BrokerStatus = 'available' | 'coming_soon' | 'beta' | 'maintenance';
export type BrokerCategory = 'popular' | 'discount' | 'full_service' | 'crypto' | 'international';
export type ConnectionMethod = 'api' | 'csv' | 'both';

export interface BrokerProvider {
  id: string;
  name: string;
  displayName: string;
  description: string;
  iconComponent: ComponentType<{ size?: number; sx?: any }>;
  status: BrokerStatus;
  category: BrokerCategory;
  connectionMethods: ConnectionMethod[];
  regions: string[];
  website: string;
  popularity: number; // 1-10 for sorting
  features: string[];
  apiDocumentation?: string;
  csvExportInstructions?: string;
  accountTypes: string[];
  minBalance?: number;
  fees: {
    trading?: string;
    account?: string;
    withdrawal?: string;
  };
}

export const BROKER_PROVIDERS: BrokerProvider[] = [
  // Currently Available
  {
    id: 'trading212',
    name: 'Trading212',
    displayName: 'Trading 212',
    description: 'Commission-free trading with stocks, ETFs, and CFDs. Popular European broker with competitive spreads.',
    iconComponent: Trading212Icon,
    status: 'available',
    category: 'popular',
    connectionMethods: ['csv', 'api'],
    regions: ['EU', 'UK'],
    website: 'https://trading212.com',
    popularity: 9,
    features: ['Commission-free stocks', 'Fractional shares', 'ISA accounts', 'CFDs', 'Auto-invest'],
    apiDocumentation: 'https://t212public-api-docs.redoc.ly/',
    csvExportInstructions: 'History → Transactions → Export → CSV',
    accountTypes: ['Invest', 'ISA', 'CFD'],
    fees: {
      trading: '0% commission',
      account: 'Free',
      withdrawal: 'Free'
    }
  },

  /*
  // Disabled for now - keeping Trading212 only

  // Coming Soon - High Priority
  {
    id: 'freetrade',
    name: 'Freetrade',
    displayName: 'Freetrade',
    description: 'UK-focused commission-free investing app. Simple interface with stocks and ETFs.',
    iconComponent: FreetradIcon,
    status: 'coming_soon',
    category: 'popular',
    connectionMethods: ['csv'],
    regions: ['UK'],
    website: 'https://freetrade.io',
    popularity: 8,
    features: ['Commission-free stocks', 'ISA accounts', 'Simple interface', 'UK focus'],
    csvExportInstructions: 'Account → History → Export',
    accountTypes: ['General', 'ISA', 'SIPP'],
    fees: {
      trading: '0% commission',
      account: 'Free',
      withdrawal: 'Free'
    }
  },

  {
    id: 'interactive_brokers',
    name: 'InteractiveBrokers',
    displayName: 'Interactive Brokers',
    description: 'Professional-grade platform with global market access and advanced tools.',
    iconComponent: InteractiveBrokersIcon,
    status: 'beta',
    category: 'full_service',
    connectionMethods: ['api', 'csv'],
    regions: ['US', 'EU', 'APAC'],
    website: 'https://interactivebrokers.com',
    popularity: 9,
    features: ['Global markets', 'Advanced tools', 'Low fees', 'Professional platform'],
    apiDocumentation: 'https://interactivebrokers.github.io/tws-api/',
    accountTypes: ['Individual', 'Margin', 'IRA'],
    minBalance: 10000,
    fees: {
      trading: '$0.005/share',
      account: '$10/month',
      withdrawal: '$0'
    }
  },

  // Major US Brokers
  {
    id: 'robinhood',
    name: 'Robinhood',
    displayName: 'Robinhood',
    description: 'Commission-free trading app popular with younger investors. Crypto and options available.',
    iconComponent: RobinhoodIcon,
    status: 'coming_soon',
    category: 'popular',
    connectionMethods: ['csv'],
    regions: ['US'],
    website: 'https://robinhood.com',
    popularity: 8,
    features: ['Commission-free', 'Crypto trading', 'Options', 'Mobile-first'],
    accountTypes: ['Individual', 'IRA'],
    fees: {
      trading: '0% commission',
      account: 'Free'
    }
  },

  {
    id: 'fidelity',
    name: 'Fidelity',
    displayName: 'Fidelity',
    description: 'Large full-service broker with excellent research tools and no account minimums.',
    iconComponent: FidelityIcon,
    status: 'coming_soon',
    category: 'full_service',
    connectionMethods: ['csv', 'api'],
    regions: ['US'],
    website: 'https://fidelity.com',
    popularity: 9,
    features: ['Research tools', 'No minimums', 'Mutual funds', 'Retirement planning'],
    accountTypes: ['Individual', 'Joint', 'IRA', '401k'],
    fees: {
      trading: '0% stocks/ETFs',
      account: 'Free'
    }
  },

  {
    id: 'schwab',
    name: 'Schwab',
    displayName: 'Charles Schwab',
    description: 'Full-service broker with comprehensive investment options and banking services.',
    iconComponent: SchwabIcon,
    status: 'coming_soon',
    category: 'full_service',
    connectionMethods: ['csv', 'api'],
    regions: ['US'],
    website: 'https://schwab.com',
    popularity: 9,
    features: ['Banking services', 'Research', 'No minimums', 'Global investing'],
    accountTypes: ['Individual', 'Joint', 'IRA', 'Trust'],
    fees: {
      trading: '0% stocks/ETFs',
      account: 'Free'
    }
  },

  // European Brokers
  {
    id: 'degiro',
    name: 'Degiro',
    displayName: 'DEGIRO',
    description: 'Low-cost European broker with access to global markets and competitive fees.',
    iconComponent: DegiroIcon,
    status: 'coming_soon',
    category: 'discount',
    connectionMethods: ['csv'],
    regions: ['EU'],
    website: 'https://degiro.com',
    popularity: 7,
    features: ['Low fees', 'Global markets', 'European focus'],
    accountTypes: ['Basic', 'Custody', 'Active'],
    fees: {
      trading: 'From €0.50',
      account: 'Free'
    }
  },

  {
    id: 'etoro',
    name: 'eToro',
    displayName: 'eToro',
    description: 'Social trading platform with copy trading features and crypto support.',
    iconComponent: EToro,
    status: 'coming_soon',
    category: 'popular',
    connectionMethods: ['csv'],
    regions: ['EU', 'UK', 'US'],
    website: 'https://etoro.com',
    popularity: 7,
    features: ['Social trading', 'Copy trading', 'Crypto', 'Fractional shares'],
    accountTypes: ['Individual', 'Professional'],
    fees: {
      trading: '0% stocks',
      withdrawal: '$5'
    }
  },

  // Other Popular Platforms
  {
    id: 'webull',
    name: 'Webull',
    displayName: 'Webull',
    description: 'Commission-free trading with advanced charting tools and extended hours trading.',
    iconComponent: WebrulIcon,
    status: 'coming_soon',
    category: 'discount',
    connectionMethods: ['csv'],
    regions: ['US'],
    website: 'https://webull.com',
    popularity: 6,
    features: ['Advanced charts', 'Extended hours', 'No minimums', 'Paper trading'],
    accountTypes: ['Individual', 'IRA'],
    fees: {
      trading: '0% commission',
      account: 'Free'
    }
  },

  {
    id: 'tastytrade',
    name: 'Tastytrade',
    displayName: 'tastytrade',
    description: 'Options-focused broker with competitive pricing for active traders.',
    iconComponent: TastytradIcon,
    status: 'coming_soon',
    category: 'full_service',
    connectionMethods: ['csv'],
    regions: ['US'],
    website: 'https://tastytrade.com',
    popularity: 6,
    features: ['Options focus', 'Active trading tools', 'Education', 'Low fees'],
    accountTypes: ['Individual', 'IRA'],
    fees: {
      trading: '$1 per contract',
      account: 'Free'
    }
  },

  {
    id: 'public',
    name: 'Public',
    displayName: 'Public',
    description: 'Social investing app with community features and no payment for order flow.',
    iconComponent: PublicIcon,
    status: 'coming_soon',
    category: 'popular',
    connectionMethods: ['csv'],
    regions: ['US'],
    website: 'https://public.com',
    popularity: 5,
    features: ['Social investing', 'No PFOF', 'Community', 'Alternative investments'],
    accountTypes: ['Individual'],
    fees: {
      trading: '0% commission',
      account: 'Free'
    }
  }
  */
];

export const getBrokerById = (id: string): BrokerProvider | undefined => {
  return BROKER_PROVIDERS.find(broker => broker.id === id);
};

export const getBrokersByCategory = (category: BrokerCategory): BrokerProvider[] => {
  return BROKER_PROVIDERS.filter(broker => broker.category === category);
};

export const getBrokersByStatus = (status: BrokerStatus): BrokerProvider[] => {
  return BROKER_PROVIDERS.filter(broker => broker.status === status);
};

export const getPopularBrokers = (limit = 6): BrokerProvider[] => {
  return BROKER_PROVIDERS
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, limit);
};

export const searchBrokers = (query: string): BrokerProvider[] => {
  const searchTerm = query.toLowerCase();
  return BROKER_PROVIDERS.filter(broker => 
    broker.name.toLowerCase().includes(searchTerm) ||
    broker.displayName.toLowerCase().includes(searchTerm) ||
    broker.description.toLowerCase().includes(searchTerm) ||
    broker.features.some(feature => feature.toLowerCase().includes(searchTerm))
  );
};

export const BROKER_CATEGORIES: { [key in BrokerCategory]: { name: string; description: string } } = {
  popular: { 
    name: 'Popular', 
    description: 'Most used brokers by our community' 
  },
  discount: { 
    name: 'Discount', 
    description: 'Low-cost brokers with competitive fees' 
  },
  full_service: { 
    name: 'Full Service', 
    description: 'Comprehensive platforms with advanced tools' 
  },
  crypto: { 
    name: 'Crypto', 
    description: 'Specialized cryptocurrency platforms' 
  },
  international: { 
    name: 'International', 
    description: 'Brokers with global market access' 
  }
};